// frontend/src/pages/ProjectDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  FolderKanban, Layers, ChevronLeft, ChevronDown, ChevronRight,
  Plus, Play, Copy, Trash2, Loader2, AlertTriangle, LogOut, Archive, LayoutDashboard, Menu,
  CheckCircle2, XCircle, Clock, RefreshCw, BarChart2, Download,
  Database, Calendar, ArrowRight, Settings2, Search,
  ArrowUp, ArrowDown, Link2Off, Zap,
} from 'lucide-react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useLanguageStore } from '../store/useLanguageStore';
import { useProjectStore } from '../store/useProjectStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { Modal } from '../components/Modal';
import { AppSidebar } from '../components/AppSidebar';
import { BulkPipelineWizard } from '../components/projects/BulkPipelineWizard';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';
import { useUIStore } from '../store/useUIStore';

// ══════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════

const PHASE_STATUS_STYLES = {
  pending: 'bg-slate-100   text-slate-500  border-slate-200',
  running: 'bg-amber-50    text-amber-700  border-amber-200',
  completed: 'bg-emerald-50  text-emerald-700 border-emerald-200',
  failed: 'bg-red-50      text-red-700    border-red-200',
  skipped: 'bg-slate-50    text-slate-400  border-slate-200',
};

const PROJECT_STATUS_STYLES = {
  draft: 'bg-slate-100  text-slate-600',
  active: 'bg-blue-50    text-blue-700',
  running: 'bg-amber-50   text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-slate-50   text-slate-400',
};

const RUN_STATUS_STYLES = {
  running: 'bg-amber-50   text-amber-700  border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50     text-red-700    border-red-200',
  aborted: 'bg-slate-100  text-slate-500  border-slate-200',
};

const formatMs = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}  ${hh}:${min}:${ss}`;
};

const formatDuration = (startMs, endMs) => {
  if (!startMs || !endMs) return '—';
  const secs = Math.round((endMs - startMs) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs % 60}s`;
};

// ══════════════════════════════════════════════════════════════════════
// SUBCOMPONENTES
// ══════════════════════════════════════════════════════════════════════

// ── Modal de fase ─────────────────────────────────────────────────────
const PhaseFormModal = ({ isOpen, phase, idProject, onClose, onSave, t }) => {
  const [form, setForm] = useState({
    name: '', description: '', phaseOrder: 1,
    dependsOnPrevious: 1, filterField: '', filterType: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(phase
        ? {
          name: phase.name, description: phase.description || '',
          phaseOrder: phase.phaseOrder, dependsOnPrevious: phase.dependsOnPrevious,
          filterField: phase.filterField || '', filterType: phase.filterType || ''
        }
        : {
          name: '', description: '', phaseOrder: 1,
          dependsOnPrevious: 1, filterField: '', filterType: ''
        }
      );
    }
  }, [isOpen, phase]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      // Fase con orden 1 nunca tiene dependencia de anterior
      dependsOnPrevious: form.phaseOrder <= 1 ? 0 : form.dependsOnPrevious,
      filterField: form.filterField || null,
      filterType: form.filterType || null,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <h3 className="text-lg font-black text-slate-900 mb-5">
          {phase ? t('projects.phase.edit') || 'Editar Fase' : t('projects.phase.new')}
        </h3>

        <div className="grid grid-cols-2 gap-4">
          {/* Nombre */}
          <div className="col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">{t('common.name') || 'Nombre'} *</label>
            <input
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              maxLength={100} autoComplete="off"
            />
          </div>

          {/* Descripción */}
          <div className="col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">{t('common.description') || 'Descripción'}</label>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={500}
            />
          </div>

          {/* Orden */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">{t('common.order') || 'Orden'}</label>
            <input type="text" inputMode="numeric" pattern="[0-9]*"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={form.phaseOrder}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '');
                setForm(f => ({ ...f, phaseOrder: val === '' ? '' : parseInt(val) }));
              }}
            />
          </div>

          {/* Dependencia */}
          <div className="flex flex-col justify-end">
            <label className={`flex items-center gap-2 text-sm ${form.phaseOrder <= 1 ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 cursor-pointer'}`}>
              <input type="checkbox"
                checked={form.phaseOrder > 1 && form.dependsOnPrevious === 1}
                disabled={form.phaseOrder <= 1}
                onChange={e => setForm(f => ({ ...f, dependsOnPrevious: e.target.checked ? 1 : 0 }))}
                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400 disabled:opacity-40"
              />
              {t('projects.phase.dependsOnPrevious')}
            </label>
            {form.phaseOrder <= 1 && (
              <p className="text-[10px] text-slate-400 mt-0.5 ml-6">
                {t('projects.phase.firstPhaseNoDependency') || 'La primera fase no tiene fase anterior.'}
              </p>
            )}
          </div>

          {/* Campo de filtro delta */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">
              {t('projects.cutoverAt') || 'Campo de filtro delta'}
            </label>
            <input
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="updatedAt, createdAt, PK..."
              value={form.filterField} onChange={e => setForm(f => ({ ...f, filterField: e.target.value }))}
              autoComplete="off"
            />
          </div>

          {/* Tipo de filtro */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">{t('common.type') || 'Tipo'}</label>
            <select
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              value={form.filterType} onChange={e => setForm(f => ({ ...f, filterType: e.target.value }))}
            >
              <option value="">— {t('common.none') || 'Ninguno'} —</option>
              <option value="date_unix">date_unix</option>
              <option value="pk">pk</option>
              <option value="watermark">watermark</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100 transition-colors">
            {t('common.buttons.close') || 'Cerrar'}
          </button>
          <button onClick={handleSave} disabled={!form.name.trim() || saving}
            className="px-5 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t('common.buttons.save') || 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal de inicio de run ────────────────────────────────────────────
const StartRunModal = ({ isOpen, idProject, onClose, onSave, t }) => {
  const [form, setForm] = useState({ runType: 'rehearsal', runLabel: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setForm({ runType: 'rehearsal', runLabel: '', notes: '' });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-black text-slate-900 mb-1">{t('projects.run.start')}</h3>
        <p className="text-sm text-slate-500 mb-5">{t('projects.subtitle')}</p>

        <div className="space-y-4">
          {/* Tipo */}
          <div className="grid grid-cols-2 gap-3">
            {['rehearsal', 'cutover'].map(type => (
              <button key={type}
                onClick={() => setForm(f => ({ ...f, runType: type }))}
                className={`p-3 rounded-xl border-2 text-sm font-bold transition text-left
                  ${form.runType === type
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
              >
                {type === 'rehearsal'
                  ? <><Play className="w-4 h-4 mb-1" />{t('projects.run.rehearsal')}</>
                  : <><Calendar className="w-4 h-4 mb-1" />{t('projects.run.cutover')}</>
                }
              </button>
            ))}
          </div>

          {form.runType === 'cutover' && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              {t('projects.run.cutover_warning') || 'El Cutover es la migración definitiva al sistema de producción. Asegúrate de haber completado todos los ejercicios previos.'}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">{t('projects.run.label')}</label>
            <input
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Ej: Ejercicio 1, Pre-cutover final..."
              value={form.runLabel} onChange={e => setForm(f => ({ ...f, runLabel: e.target.value }))}
              maxLength={100} autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">{t('projects.run.notes')}</label>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} maxLength={500}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100 transition-colors">
            {t('common.buttons.close') || 'Cerrar'}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <Play className="w-4 h-4" />
            {t('projects.run.start')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Panel de Benchmark ────────────────────────────────────────────────
const BenchmarkPanel = ({ benchmark, isLoading, onClose, onExport, t }) => {
  // ── ESC para cerrar ──
  React.useEffect(() => {
    if (!benchmark && !isLoading) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [benchmark, isLoading, onClose]);

  if (isLoading) return (
    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>
  );
  if (!benchmark) return null;

  const maxDur = Math.max(...benchmark.pipelines.map(p => p.durationSeconds), 0.001);
  const totalErrors = benchmark.pipelines.reduce((s, p) => s + (p.rowsFailed || 0), 0);
  const fmtDuration = (sec) =>
    sec < 1 ? `${sec.toFixed(1)}s`
      : sec < 60 ? `${Math.round(sec)}s`
        : `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Card: en mobile ocupa 100% abajo (bottom-sheet), en desktop centrado */}
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-2xl shadow-xl
        rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-slate-200 shrink-0">
          <div className="min-w-0 flex-1 mr-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-indigo-600 shrink-0" />
              <span className="truncate">{t('projects.benchmark.title')}</span>
            </h3>
            {benchmark.runLabel && (
              <p className="text-[10px] text-slate-500 mt-0.5 truncate">{benchmark.runLabel}</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* Botones de descarga */}
            <button
              onClick={() => onExport && onExport('pdf')}
              className="flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold text-red-600 hover:bg-red-50 rounded-lg transition"
              title={t('projects.benchmark_export.pdf') || 'Descargar PDF'}
            >
              <Download className="w-3.5 h-3.5" />
              PDF
            </button>
            <button
              onClick={() => onExport && onExport('xlsx')}
              className="flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
              title={t('projects.benchmark_export.excel') || 'Descargar Excel'}
            >
              <Download className="w-3.5 h-3.5" />
              Excel
            </button>
            {/* Botón X — área de tap 44x44 (mínimo recomendado para touch) */}
            <button
              onClick={onClose}
              className="p-2.5 -mr-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* KPIs — 2x2 en mobile, 4 en desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 md:px-5 py-3 border-b border-slate-100 bg-slate-50 shrink-0">
          {[
            { label: t('dashboard.kpi.volume') || 'Registros', value: benchmark.totalRows.toLocaleString() },
            { label: t('common.errors') || 'Errores', value: totalErrors.toLocaleString(), warn: totalErrors > 0 },
            { label: 'MB', value: `${benchmark.totalMB}` },
            { label: t('common.duration') || 'Duración', value: fmtDuration(benchmark.totalDurationSec) },
          ].map(({ label, value, warn }) => (
            <div key={label} className="text-center py-1">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
              <p className={`text-base md:text-lg font-black ${warn ? 'text-red-600' : 'text-slate-800'}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Lista de pipelines — scrollable, touch-friendly */}
        <div className="overflow-y-auto flex-1 min-h-0 px-4 md:px-5 py-3 overscroll-contain">
          {benchmark.pipelines.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-6">
              {t('common.no_data') || 'Sin datos de pipelines para este run.'}
            </p>
          ) : (
            <div className="space-y-2">
              {benchmark.pipelines.map(pl => (
                <div key={pl.idConfigHead}
                  className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200"
                >
                  {/* Nombre + status */}
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-800 truncate">{pl.pipelineName}</p>
                      {pl.phaseName && (
                        <p className="text-[9px] text-slate-500 truncate">
                          {t('projects.phaseName')} {pl.phaseOrder}: {pl.phaseName}
                        </p>
                      )}
                    </div>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border shrink-0
                      ${pl.status === 'SUCCESS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : pl.status === 'PARTIAL_SUCCESS' ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-red-50 text-red-700 border-red-200'}`}>
                      {pl.status}
                    </span>
                  </div>

                  {/* Barra de duración */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-200 rounded-full h-1">
                      <div
                        className={`h-1 rounded-full transition-all ${pl.status === 'FAILED' ? 'bg-red-400' : 'bg-indigo-500'
                          }`}
                        style={{ width: `${Math.max(Math.round((pl.durationSeconds / maxDur) * 100), 2)}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-slate-500 font-mono w-12 text-right shrink-0">
                      {fmtDuration(pl.durationSeconds)}
                    </span>
                  </div>

                  {/* Métricas inline */}
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                    <span className="text-[9px] text-slate-500">✓ {pl.rowsProcessed.toLocaleString()}</span>
                    {pl.rowsFailed > 0 && (
                      <span className="text-[9px] text-red-500">✗ {pl.rowsFailed.toLocaleString()}</span>
                    )}
                    <span className="text-[9px] text-slate-400">{pl.mbProcessed} MB</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer — hint */}
        <div className="px-4 md:px-5 py-2 border-t border-slate-100 bg-white shrink-0 text-center">
          <span className="text-[9px] text-slate-400">
            {t('wizard.pipeline_graph.esc_hint') || 'ESC o clic fuera para cerrar'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── PipelinePickerModal ───────────────────────────────────────────────
const PipelinePickerModal = ({
  isOpen, phase, idProject, allPipelines, isFetchingPipelines, onClose, onAssign, t,
}) => {
  const [search, setSearch] = useState('');
  const [phaseOrder, setPhaseOrder] = useState(1);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelected(new Set());
      const currentlyAssigned = allPipelines.filter(
        p => p.idProjectPhase === phase?.idProjectPhase
      );
      setPhaseOrder(currentlyAssigned.length + 1);
    }
  }, [isOpen, allPipelines, phase]);

  // ── ESC para cerrar ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const assigned = allPipelines.filter(p => p.idProjectPhase === phase.idProjectPhase);
  const available = allPipelines.filter(p =>
    !p.idProject && p.status === 1 &&
    p.name.toLowerCase().includes(search.toLowerCase())
  );
  const otherProj = allPipelines.filter(p =>
    p.idProject && p.idProject !== idProject && p.status === 1 &&
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const PipelineRow = ({ pipeline, badge, badgeStyle, selectable }) => (
    <div
      // onClick={() => selectable && setSelected(pipeline.id === selected ? null : pipeline.id)}
      onClick={() => {
        if (!selectable) return;
        setSelected(prev => {
          const next = new Set(prev);
          next.has(pipeline.id) ? next.delete(pipeline.id) : next.add(pipeline.id);
          return next;
        });
      }}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition
        ${selectable ? 'cursor-pointer' : 'cursor-default'}
        ${selected.has(pipeline.id)
          ? 'border-indigo-400 bg-indigo-50'
          : selectable
            ? 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50 active:bg-indigo-50'
            : 'border-slate-100 bg-slate-50 opacity-60'}`}
    >
      {selectable && (
        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0
          ${selected.has(pipeline.id) ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`}>
          {selected.has(pipeline.id) && <div className="w-2.5 h-2.5 bg-white rounded-sm" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-slate-800 truncate">
          {pipeline.name} <span className="font-normal text-slate-400">(#{pipeline.id})</span>
        </p>
        <p className="text-[10px] text-slate-500">
          {pipeline.sourceType?.toUpperCase()} → {pipeline.targetType?.toUpperCase()}
        </p>
      </div>
      <span className={`text-[9px] font-bold px-2 py-0.5 rounded border shrink-0 ${badgeStyle}`}>
        {badge}
      </span>
    </div>
  );

  const Section = ({ title, children, empty }) => (
    <div className="mb-4">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">{title}</p>
      {children || (
        <p className="text-xs text-slate-400 italic px-1">{empty}</p>
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Bottom-sheet en mobile, centrado en desktop */}
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-lg shadow-xl
        rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0 flex-1 mr-3">
            <h3 className="text-sm sm:text-base font-black text-slate-900 truncate">
              {t('projects.phase.add_pipeline') || 'Añadir pipeline a fase'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {phase.phaseOrder}. {phase.name}
            </p>
          </div>
          <button onClick={onClose}
            className="p-2.5 -mr-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Búsqueda */}
        <div className="px-4 sm:px-6 pt-3 pb-2 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder={t('common.search') || 'Buscar pipeline...'}
              value={search} onChange={e => setSearch(e.target.value)}
              autoComplete="off" autoFocus
            />
          </div>
        </div>

        {/* Lista scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-2 overscroll-contain">
          {isFetchingPipelines ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
            </div>
          ) : (
            <>
              {assigned.length > 0 && (
                <Section title={t('projects.phase.already_assigned') || 'Ya en esta fase'}>
                  <div className="space-y-1.5">
                    {assigned.map(p => (
                      <PipelineRow key={p.id} pipeline={p}
                        badge="Asignado" badgeStyle="bg-indigo-50 text-indigo-700 border-indigo-200"
                        selectable={false} />
                    ))}
                  </div>
                </Section>
              )}

              <Section
                title={t('projects.phase.available') || 'Disponibles (sin proyecto)'}
                empty={t('projects.phase.none_available') || 'Sin pipelines disponibles'}
              >
                {available.length > 0 && (
                  <div className="space-y-1.5">
                    {available.map(p => (
                      <PipelineRow key={p.id} pipeline={p}
                        badge="Disponible" badgeStyle="bg-emerald-50 text-emerald-700 border-emerald-200"
                        selectable />
                    ))}
                  </div>
                )}
              </Section>

              {otherProj.length > 0 && (
                <Section title={t('projects.phase.other_project') || 'En otro proyecto (se reasignará)'}>
                  <div className="space-y-1.5">
                    {otherProj.map(p => (
                      <PipelineRow key={p.id} pipeline={p}
                        badge="Otro proyecto" badgeStyle="bg-amber-50 text-amber-700 border-amber-200"
                        selectable />
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>

        {/* Footer — stack en mobile, inline en desktop */}
        <div className="border-t border-slate-200 px-4 sm:px-6 py-3 sm:py-4 shrink-0
          flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-600 whitespace-nowrap">
              {t('common.order') || 'Orden en fase'}:
            </label>
            <input type="text" inputMode="numeric" pattern="[0-9]*"
              className="w-16 border border-slate-300 rounded-lg px-2 py-2 sm:py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 text-center"
              value={phaseOrder}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '');
                setPhaseOrder(val === '' ? '' : parseInt(val));
              }}
            />
          </div>
          <div className="flex gap-2 sm:ml-auto">
            <button onClick={onClose}
              className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100 transition border border-slate-200 sm:border-0">
              {t('common.buttons.cancel') || 'Cancelar'}
            </button>
            <button
              onClick={() => selected.size > 0 && onAssign([...selected], phaseOrder || 1)}
              disabled={selected.size === 0}
              className="flex-1 sm:flex-none px-5 py-2.5 sm:py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {selected.size > 1
                ? `${t('projects.phase.assign') || 'Asignar'} (${selected.size})`
                : t('projects.phase.assign') || 'Asignar'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Fila de pipeline dentro de una fase ──────────────────────────────
const PhasePipelineRow = ({
  pipeline, isFirst, isLast, onExecute, onUnassign, onMoveUp, onMoveDown, t, navigate,
}) => {
  const statusDot = {
    1: 'bg-emerald-400', 2: 'bg-amber-400', 0: 'bg-slate-300', 3: 'bg-blue-400',
  }[pipeline.status] ?? 'bg-slate-300';

  return (
    <div className="flex items-center gap-2 sm:gap-3 px-3 py-2.5 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition group">
      {/* Orden visual */}
      <div className="flex flex-col items-center gap-0.5 shrink-0">
        <button onClick={onMoveUp} disabled={isFirst}
          className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-0 transition">
          <ArrowUp className="w-3 h-3" />
        </button>
        <span className="text-[10px] font-black text-slate-400 w-5 text-center">
          {pipeline.phaseOrder ?? '—'}
        </span>
        <button onClick={onMoveDown} disabled={isLast}
          className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-0 transition">
          <ArrowDown className="w-3 h-3" />
        </button>
      </div>

      {/* Info del pipeline */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className={`w-2 h-2 rounded-full shrink-0 ${statusDot}`} />
        <div className="min-w-0">
          <p className="text-xs font-bold text-slate-800 truncate">
            {pipeline.name} <span className="font-normal text-slate-400">(#{pipeline.id})</span>
          </p>
          <p className="text-[10px] text-slate-500 truncate">
            {pipeline.sourceType?.toUpperCase()} → {pipeline.targetType?.toUpperCase()}
            {pipeline.frequency && ` · ${pipeline.frequency}`}
          </p>
        </div>
      </div>

      {/* Acciones — siempre visibles en mobile, hover en desktop */}
      <div className="flex items-center gap-0.5 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <button onClick={() => navigate(`/migracion?id=${pipeline.id}`)}
          className="p-2 sm:p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
          title={t('projects.phase.edit_pipeline') || 'Editar pipeline'}>
          <Settings2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
        <button onClick={() => navigate('/dashboard')}
          className="p-2 sm:p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition"
          title={t('projects.phase.view_dashboard') || 'Ver en Dashboard'}>
          <LayoutDashboard className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
        <button onClick={() => onExecute(pipeline.id)}
          className="p-2 sm:p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
          title={t('dashboard.actions.force_btn') || 'Ejecutar'}>
          <Play className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
        <button onClick={() => onUnassign(pipeline.id)}
          className="p-2 sm:p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
          title={t('projects.phase.unassign') || 'Desasignar'}>
          <Link2Off className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ── Acordeón de fase ──────────────────────────────────────────────────
const PhaseAccordion = ({
  phase, isExpanded, onToggle, onEdit, onDelete,
  phasePipelines, allPipelines, isFetchingPipelines,
  onOpenPicker, onExecutePipeline, onUnassignPipeline, onReorderPipeline,
  idProject, navigate, t,
}) => {
  const phaseStyle = PHASE_STATUS_STYLES[phase.status] || PHASE_STATUS_STYLES.pending;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Cabecera */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <span className="text-xs font-black text-indigo-600">{phase.phaseOrder}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-black text-slate-900 truncate">{phase.name}</p>
            {phase.description && (
              <p className="text-xs text-slate-500 truncate">{phase.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          {phasePipelines.length > 0 && (
            <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 font-bold">
              {phasePipelines.length} pipeline{phasePipelines.length !== 1 ? 's' : ''}
            </span>
          )}
          {phase.dependsOnPrevious === 1 && (
            <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded border border-slate-200 hidden sm:inline">
              {t('projects.phase.dependsOnPrevious')}
            </span>
          )}
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${phaseStyle}`}>
            {t(`projects.phase.status.${phase.status}`) || phase.status}
          </span>
          {isExpanded
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {/* Detalle expandido */}
      {isExpanded && (
        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50 space-y-4">

          {/* Meta de la fase + acciones de edición */}
          <div className="flex items-center justify-between">
            <div className="flex gap-4 flex-wrap">
              {phase.filterField && (
                <span className="text-xs text-slate-600">
                  <span className="font-bold">Delta:</span> {phase.filterField}
                  {phase.filterType && <span className="ml-1 text-slate-400">({phase.filterType})</span>}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => onEdit(phase)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                title={t('projects.phase.edit_phase') || 'Editar fase'}>
                <Settings2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t('projects.phase.edit_phase') || 'Editar fase'}</span>
              </button>
              {phase.status !== 'running' && (
                <button onClick={() => onDelete(phase)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                  title={t('projects.phase.delete_phase') || 'Anular fase'}>
                  <Trash2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t('projects.phase.delete_phase') || 'Anular fase'}</span>
                </button>
              )}
            </div>
          </div>

          {/* Lista de pipelines asignados */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                {t('projects.phase.pipelines') || 'Pipelines'}
              </p>
              <button
                onClick={() => onOpenPicker(phase)}
                className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 hover:text-indigo-800 transition"
              >
                <Plus className="w-3 h-3" />
                {t('projects.phase.add_pipeline') || 'Añadir'}
              </button>
            </div>

            {phasePipelines.length === 0 ? (
              <div className="bg-white rounded-lg border border-dashed border-slate-300 py-5 text-center">
                <p className="text-xs text-slate-400 italic">
                  {t('projects.phase.no_pipelines') || 'Sin pipelines asignados.'}
                </p>
                <button onClick={() => onOpenPicker(phase)}
                  className="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition flex items-center gap-1 mx-auto">
                  <Plus className="w-3.5 h-3.5" />
                  {t('projects.phase.add_pipeline') || 'Añadir pipeline'}
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                {phasePipelines
                  .slice()
                  .sort((a, b) => (a.phaseOrder ?? 999) - (b.phaseOrder ?? 999))
                  .map((pl, idx, arr) => (
                    <PhasePipelineRow
                      key={pl.id}
                      pipeline={pl}
                      isFirst={idx === 0}
                      isLast={idx === arr.length - 1}
                      navigate={navigate}
                      t={t}
                      onExecute={onExecutePipeline}
                      onUnassign={(chId) => onUnassignPipeline(phase.idProjectPhase, chId)}
                      onMoveUp={() => onReorderPipeline(
                        phase.idProjectPhase, pl.id,
                        Math.max(1, (pl.phaseOrder ?? idx + 1) - 1)
                      )}
                      onMoveDown={() => onReorderPipeline(
                        phase.idProjectPhase, pl.id,
                        (pl.phaseOrder ?? idx + 1) + 1
                      )}
                    />
                  ))
                }
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ══════════════════════════════════════════════════════════════════════

export const ProjectDetail = ({ user, setUser }) => {
  const { idProject } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguageStore();
  const {
    currentProject, runs, benchmark,
    allPipelines, isFetchingPipelines,
    isLoadingDetail, isLoadingRuns, isLoadingBenchmark, isSaving, error,
    fetchProjectDetail, fetchRuns, fetchBenchmark, startRun, updateProject,
    createPhase, updatePhase, deletePhase,
    fetchPipelinesForPicker, assignPipeline, unassignPipeline, reorderPipelineInPhase,
    expandedPhases, togglePhaseExpanded, expandAllPhases, collapseAllPhases,
    clearError, clearBenchmark,
  } = useProjectStore();

  const [phaseModal, setPhaseModal] = useState({ open: false, phase: null });
  const [pickerPhase, setPickerPhase] = useState(null);   // fase activa del picker
  const [runModal, setRunModal] = useState(false);
  const [bulkWizardOpen, setBulkWizardOpen] = useState(false);
  const [benchmarkId, setBenchmarkId] = useState(null);
  const [modalConfig, setModalConfig] = useState({ isOpen: false });

  const closeModal = () => setModalConfig({ isOpen: false });
  const { openSidebar } = useUIStore();

  const load = useCallback(async () => {
    await fetchProjectDetail(idProject);
    await fetchRuns(idProject);
    await fetchPipelinesForPicker();
  }, [idProject]);

  useEffect(() => { load(); }, [load]);

  // ── Auto-poll mientras haya un run activo ────────────────────
  // Polling cada 5s SOLO cuando hay un run "running".
  // Se detiene automáticamente cuando todos los runs terminan.
  useEffect(() => {
    const hasActiveRun = runs.some(r => r.status === 'running');
    if (!hasActiveRun) return;

    const interval = setInterval(async () => {
      await fetchProjectDetail(idProject);
      await fetchRuns(idProject);
    }, 5000);

    return () => clearInterval(interval);
  }, [runs, idProject, fetchProjectDetail, fetchRuns]);

  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
    navigate('/login');
  };

  // ── Guardar fase ───────────────────────────────────────────────────
  const handleSavePhase = async (formData) => {
    const { phase } = phaseModal;
    const result = phase
      ? await updatePhase(idProject, phase.idProjectPhase, formData)
      : await createPhase(idProject, formData);

    if (result.ok) {
      setPhaseModal({ open: false, phase: null });
    } else {
      setModalConfig({
        isOpen: true, showCancel: false,
        title: t('common.error') || 'Error',
        message: result.error, onAccept: closeModal,
      });
    }
  };

  // ── Eliminar fase ──────────────────────────────────────────────────
  const handleDeletePhase = (phase) => {
    const phasePipelineCount = allPipelines.filter(
      p => p.idProjectPhase === phase.idProjectPhase && p.status === 1
    ).length;
    const pipelinesMsg = phasePipelineCount > 0
      ? `\n\n${phasePipelineCount} pipeline(s) asignado(s) quedarán libres y disponibles para reasignar.`
      : '';
    setModalConfig({
      isOpen: true, showCancel: true,
      title: t('projects.phase.delete_phase') || 'Anular fase',
      message: `¿Anular definitivamente la fase "${phase.name}"?\n\nEsta acción no se puede deshacer.${pipelinesMsg}`,
      onAccept: async () => {
        closeModal();
        const result = await deletePhase(idProject, phase.idProjectPhase);
        if (!result.ok) {
          setModalConfig({
            isOpen: true, showCancel: false,
            title: t('common.error') || 'Error',
            message: result.error, onAccept: closeModal,
          });
        }
      },
    });
  };

  // ── Iniciar run ────────────────────────────────────────────────────
  const handleStartRun = async (formData) => {
    const result = await startRun(idProject, formData);
    setRunModal(false);
    if (result.ok) {
      setModalConfig({
        isOpen: true, showCancel: false,
        title: '✅ ' + (t('projects.run.started') || 'Run iniciado'),
        message: t('projects.run.started_msg') || 'Las fases del proyecto comenzarán a ejecutarse. Recibirás un email al completarse cada fase.',
        onAccept: () => { closeModal(); load(); },
      });
    } else {
      setModalConfig({
        isOpen: true, showCancel: false,
        title: t('common.error') || 'Error',
        message: result.error, onAccept: closeModal,
      });
    }
  };

  // ── Ver benchmark ──────────────────────────────────────────────────
  const handleViewBenchmark = async (idProjectRun) => {
    setBenchmarkId(idProjectRun);
    await fetchBenchmark(idProject, idProjectRun);
  };

  // ── Exportar benchmark (PDF / Excel) ───────────────────────────────
  const handleExportBenchmark = async (format) => {
    if (!benchmarkId) return;
    try {
      const { apiClient } = await import('../api/client');
      const response = await apiClient.get(
        `/projects/${idProject}/runs/${benchmarkId}/benchmark/export`,
        { params: { format }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      link.download = `benchmark_run${benchmarkId}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setModalConfig({
        isOpen: true, showCancel: false,
        title: t('common.error') || 'Error',
        message: t('projects.benchmark_export.error') || 'Error al descargar el reporte.',
        onAccept: closeModal,
      });
    }
  };

  // ── Asignar pipeline a fase ────────────────────────────────────────
  const handleAssignPipeline = async (idConfigHeads, startOrder) => {
    if (!pickerPhase) return;
    // Asignar múltiples pipelines con phaseOrder incremental
    const ids = Array.isArray(idConfigHeads) ? idConfigHeads : [idConfigHeads];
    let errorMsg = null;
    for (let i = 0; i < ids.length; i++) {
      const result = await assignPipeline(
        idProject, pickerPhase.idProjectPhase, ids[i], { phaseOrder: startOrder + i }
      );
      if (!result.ok) { errorMsg = result.error; break; }
    }
    if (errorMsg) {
      setModalConfig({
        isOpen: true, showCancel: false,
        title: t('common.error') || 'Error',
        message: errorMsg, onAccept: closeModal,
      });
    }
    // Refrescar lista (modal sigue abierto para seguir asignando)
    await fetchPipelinesForPicker();
    setPickerPhase(null);
  };

  // ── Desasignar pipeline de fase ────────────────────────────────────
  const handleUnassignPipeline = async (idProjectPhase, idConfigHead) => {
    const result = await unassignPipeline(idProject, idProjectPhase, idConfigHead);
    if (result.ok) {
      await fetchPipelinesForPicker();
    } else {
      setModalConfig({
        isOpen: true, showCancel: false,
        title: t('common.error') || 'Error',
        message: result.error, onAccept: closeModal,
      });
    }
  };

  // ── Reordenar pipeline dentro de una fase ─────────────────────────
  const handleReorderPipeline = async (idProjectPhase, idConfigHead, newOrder) => {
    // Buscar el pipeline que actualmente ocupa la posición destino (swap)
    const phasePipelines = allPipelines.filter(p => p.idProjectPhase === idProjectPhase);
    const moving = phasePipelines.find(p => p.id === idConfigHead);
    const occupant = phasePipelines.find(p => p.id !== idConfigHead && p.phaseOrder === newOrder);

    // Swap: el que estaba en la posición destino toma la posición origen
    if (occupant && moving) {
      await reorderPipelineInPhase(idProject, idProjectPhase, occupant.id, moving.phaseOrder);
    }
    await reorderPipelineInPhase(idProject, idProjectPhase, idConfigHead, newOrder);
    await fetchPipelinesForPicker();
  };

  // ── Forzar ejecución individual ────────────────────────────────────
  const handleExecutePipeline = async (idConfigHead) => {
    setModalConfig({
      isOpen: true, showCancel: true,
      title: t('dashboard.actions.force_title') || 'Ejecutar pipeline',
      message: t('dashboard.actions.force_msg_1') || '¿Ejecutar este pipeline ahora?',
      onAccept: async () => {
        closeModal();
        try {
          const { apiClient } = await import('../api/client');
          await apiClient.post(`/dashboard/pipelines/${idConfigHead}/execute`);
          setModalConfig({
            isOpen: true, showCancel: false,
            title: '✅ ' + (t('dashboard.actions.queued_title') || 'Pipeline encolado'),
            message: t('dashboard.actions.queued_msg') || 'El pipeline se ejecutará en breve.',
            onAccept: () => { closeModal(); load(); },
          });
        } catch (err) {
          setModalConfig({
            isOpen: true, showCancel: false,
            title: t('common.error') || 'Error',
            message: err.message, onAccept: closeModal,
          });
        }
      },
    });
  };

  const phases = currentProject?.phases || [];
  const allDone = phases.length > 0 && phases.every(ph => ['completed', 'failed', 'skipped'].includes(ph.status));
  const hasRun = runs.some(r => r.status === 'running');

  const projectStatusStyle = PROJECT_STATUS_STYLES[currentProject?.status] || PROJECT_STATUS_STYLES.draft;

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8FAFC]">

      {/* ── HEADER ───────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={openSidebar}
              aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow"
                className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow"
                className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
            {/* Breadcrumb */}
            {/* Breadcrumb */}
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-500 border-l border-slate-200 pl-3 ml-1">
              <Link to="/projects" className="hover:text-indigo-600 transition font-semibold">
                {t('projects.title')}
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-800 font-bold truncate max-w-[200px]">
                {currentProject?.name || '...'}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{user?.name}</p>
              </div>
              <button onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('dashboard.logout') || 'Cerrar sesión'}>
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── MAIN ─────────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in">

        {/* Back + error */}
        <button onClick={() => navigate('/projects')}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 font-semibold mb-5 transition">
          <ChevronLeft className="w-4 h-4" /> {t('common.back') || 'Volver'}
        </button>

        {error && (
          <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
            <button onClick={clearError} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        {isLoadingDetail ? (
          <div className="flex justify-center items-center py-24">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        ) : currentProject ? (

          <div className="space-y-8">

            {/* ── CABECERA DEL PROYECTO ─────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                    <FolderKanban className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-xl font-black text-slate-900">{currentProject.name}</h1>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${projectStatusStyle}`}>
                        {t(`projects.status.${currentProject.status}`) || currentProject.status}
                      </span>
                    </div>
                    {currentProject.description && (
                      <p className="text-sm text-slate-500 mt-1">{currentProject.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" />
                        {phases.length} {t('projects.phaseName') || 'fase(s)'}
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {t(`projects.cutoverMode.${currentProject.cutoverMode}`) || currentProject.cutoverMode}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Acciones del proyecto */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={load}
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Refrescar">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  {!hasRun && currentProject.status !== 'archived' && (
                    <button
                      onClick={() => setRunModal(true)}
                      disabled={phases.length === 0}
                      className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-sm transition disabled:opacity-50"
                    >
                      <Play className="w-4 h-4" />
                      {t('projects.run.start')}
                    </button>
                  )}
                  {hasRun && (
                    <span className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2 rounded-xl font-bold text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('projects.status.running')}
                    </span>
                  )}
                  {currentProject.status !== 'archived' && currentProject.status !== 'running' && (
                    <button
                      onClick={() => setModalConfig({
                        isOpen: true, showCancel: true,
                        title: t('projects.archive') || 'Archivar proyecto',
                        message: t('projects.archive_confirm') || '¿Archivar este proyecto? No aparecerá en la lista activa pero se conservarán los datos.',
                        onAccept: async () => {
                          closeModal();
                          const result = await updateProject(parseInt(idProject), { status: 'archived' });
                          if (result.ok) navigate('/projects');
                        },
                      })}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
                      title={t('projects.archive') || 'Archivar'}
                    >
                      <Archive className="w-4 h-4" />
                      {t('projects.archive') || 'Archivar'}
                    </button>
                  )}
                  {/* Reactivar — solo para completed o archived */}
                  {(currentProject.status === 'completed' || currentProject.status === 'archived') && (
                    <button
                      onClick={() => setModalConfig({
                        isOpen: true, showCancel: true,
                        title: t('projects.reactivate') || 'Reactivar proyecto',
                        message: t('projects.reactivate_confirm') || '¿Reactivar este proyecto? Volverá al estado activo y podrás ejecutar nuevos ejercicios.',
                        onAccept: async () => {
                          closeModal();
                          const result = await updateProject(parseInt(idProject), { status: 'active' });
                          if (result.ok) load();
                        },
                      })}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-xl transition"
                      title={t('projects.reactivate') || 'Reactivar'}
                    >
                      <RefreshCw className="w-4 h-4" />
                      {t('projects.reactivate') || 'Reactivar'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ── FASES ────────────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-black text-slate-800 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-indigo-600" />
                  {t('projects.phase.title') || 'Fases del Proyecto'}
                </h2>
                <div className="flex items-center gap-2">
                  {phases.length > 1 && (
                    <>
                      <button onClick={expandAllPhases}
                        className="text-xs text-slate-500 hover:text-indigo-600 font-semibold transition">
                        {t('common.expand_all') || 'Expandir todo'}
                      </button>
                      <span className="text-slate-300">|</span>
                      <button onClick={collapseAllPhases}
                        className="text-xs text-slate-500 hover:text-indigo-600 font-semibold transition">
                        {t('common.collapse_all') || 'Colapsar todo'}
                      </button>
                      <span className="text-slate-300">|</span>
                    </>
                  )}
                  <button
                    onClick={() => setBulkWizardOpen(true)}
                    className="flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-800 transition"
                  >
                    <Zap className="w-3.5 h-3.5" /> {t('projects.bulk.button') || 'Crear en lote'}
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    onClick={() => setPhaseModal({ open: true, phase: null })}
                    className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition"
                  >
                    <Plus className="w-3.5 h-3.5" /> {t('projects.phase.new')}
                  </button>
                </div>
              </div>

              {phases.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 py-12 flex flex-col items-center text-slate-500">
                  <Layers className="w-10 h-10 text-slate-300 mb-3" />
                  <p className="text-sm">{t('projects.phase.empty') || 'Sin fases definidas.'}</p>
                  <button
                    onClick={() => setPhaseModal({ open: true, phase: null })}
                    className="mt-4 flex items-center gap-1 text-sm font-bold text-indigo-600 hover:text-indigo-800 transition"
                  >
                    <Plus className="w-4 h-4" /> {t('projects.phase.new')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {phases.map((phase, idx) => (
                    <div key={phase.idProjectPhase} className="flex items-stretch gap-2">
                      {/* Conector vertical entre fases */}
                      <div className="flex flex-col items-center w-6 shrink-0">
                        {idx > 0 && <div className="w-0.5 h-3 bg-slate-200" />}
                        <div className={`w-2 h-2 rounded-full mt-1 shrink-0
                          ${phase.status === 'completed' ? 'bg-emerald-400'
                            : phase.status === 'running' ? 'bg-amber-400'
                              : phase.status === 'failed' ? 'bg-red-400'
                                : 'bg-slate-300'}`}
                        />
                        {idx < phases.length - 1 && phase.dependsOnPrevious === 1 && (
                          <div className="w-0.5 flex-1 bg-slate-200 mt-1" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <PhaseAccordion
                          phase={phase}
                          isExpanded={expandedPhases.has(phase.idProjectPhase)}
                          onToggle={() => togglePhaseExpanded(phase.idProjectPhase)}
                          onEdit={(ph) => setPhaseModal({ open: true, phase: ph })}
                          onDelete={handleDeletePhase}
                          phasePipelines={allPipelines.filter(
                            p => p.idProjectPhase === phase.idProjectPhase && p.status === 1
                          )}
                          allPipelines={allPipelines}
                          isFetchingPipelines={isFetchingPipelines}
                          onOpenPicker={setPickerPhase}
                          onExecutePipeline={handleExecutePipeline}
                          onUnassignPipeline={handleUnassignPipeline}
                          onReorderPipeline={handleReorderPipeline}
                          idProject={idProject}
                          navigate={navigate}
                          t={t}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── HISTORIAL DE RUNS ─────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-black text-slate-800 flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-indigo-600" />
                  {t('projects.run.history') || 'Historial de Ejecuciones'}
                </h2>
                <button onClick={() => fetchRuns(idProject)}
                  className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>

              {isLoadingRuns ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>
              ) : runs.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 py-10 text-center text-slate-500 text-sm">
                  {t('projects.run.empty') || 'Sin ejecuciones todavía. Inicia el primer ejercicio.'}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {['#', t('common.type') || 'Tipo', t('projects.run.label'), t('common.start') || 'Inicio', t('common.duration') || 'Duración', 'Estado', ''].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {runs.map(run => (
                        <tr key={run.idProjectRun} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-xs font-mono text-slate-400">#{run.idProjectRun}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border
                              ${run.runType === 'cutover'
                                ? 'bg-violet-50 text-violet-700 border-violet-200'
                                : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                              {t(`projects.run.${run.runType}`) || run.runType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">{run.runLabel || '—'}</td>
                          <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formatMs(run.startedAt)}</td>
                          <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                            {formatDuration(run.startedAt, run.completedAt)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border
                              ${RUN_STATUS_STYLES[run.status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                              {t(`projects.status.${run.status}`) || run.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {run.status !== 'running' && (
                              <button
                                onClick={() => handleViewBenchmark(run.idProjectRun)}
                                className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition whitespace-nowrap"
                              >
                                <BarChart2 className="w-3.5 h-3.5" />
                                {t('projects.benchmark.title')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        ) : !error && (
          <div className="flex justify-center items-center py-24 text-slate-500 text-sm">
            {t('common.no_data') || 'Proyecto no encontrado.'}
          </div>
        )}
      </main>

      {/* ── MODALES ──────────────────────────────────────────────── */}
      <PipelinePickerModal
        isOpen={Boolean(pickerPhase)}
        phase={pickerPhase || {}}
        idProject={parseInt(idProject)}
        allPipelines={allPipelines}
        isFetchingPipelines={isFetchingPipelines}
        onClose={() => setPickerPhase(null)}
        onAssign={handleAssignPipeline}
        t={t}
      />
      <PhaseFormModal
        isOpen={phaseModal.open}
        phase={phaseModal.phase}
        idProject={idProject}
        onClose={() => setPhaseModal({ open: false, phase: null })}
        onSave={handleSavePhase}
        t={t}
      />

      <StartRunModal
        isOpen={runModal}
        idProject={idProject}
        onClose={() => setRunModal(false)}
        onSave={handleStartRun}
        t={t}
      />

      <BenchmarkPanel
        benchmark={benchmark}
        isLoading={isLoadingBenchmark}
        onClose={() => { clearBenchmark(); setBenchmarkId(null); }}
        onExport={handleExportBenchmark}
        t={t}
      />

      <Modal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        onClose={closeModal}
        onAccept={modalConfig.onAccept}
        showCancel={modalConfig.showCancel}
      />

      <BulkPipelineWizard
        isOpen={bulkWizardOpen}
        idProject={idProject}
        phases={phases}
        onClose={() => setBulkWizardOpen(false)}
        onComplete={(result) => {
          setBulkWizardOpen(false);
          load(); // Recargar proyecto con los nuevos pipelines
          fetchPipelinesForPicker(); // Actualizar lista de pipelines
        }}
      />

      {/* Drawer de navegación global */}
      <AppSidebar user={user} onLogout={handleLogout} />
    </div>
  );
};

export default ProjectDetail;
